import { Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, map, Observable, ReplaySubject, Subject } from 'rxjs';
import { LexbeDoc } from '@models/doc.model';
import { RedactionAnnotationEdit } from '../../components/review/redaction-annotation/redaction-annotation-edit.model';
import { MainTabIndices } from '@enums/main-tab-indices.enum';
import { cloneDeep } from 'lodash-es';
import { take } from 'rxjs/operators';
import { ComparisonUtility } from '@utilities/comparison.utility';
import { UntypedFormGroup } from '@angular/forms';
import { TabPreference } from '@models/review/tab-preference';
import { DocNote } from '@models/ui/doc-note.model';
import { Custodian } from '@models/review/custodian.model';
import { Audit } from '@models/review/audit.model';
import { Folder } from '@models/ui/folder.model';
import { ReviewEntry } from '@models/ui/review-entry.model';
import { Fact } from '@models/ui/fact.model';
import { Issue } from '@models/ui/issue.model';
import { SplitDocResource } from '@models/resource/split-doc.resource';
import { DocChangeSetReferenceResource } from '@models/resource/doc-change-set-reference.resource';
import { DocViewerContextDoc } from '@models/ui/doc-viewer-context-doc.model';

@Injectable({
	providedIn: 'root'
})
export class DocViewerStore {

	public documentDataFormGroup: UntypedFormGroup;
	public documentCodingFormGroup: UntypedFormGroup;
	public documentReviewFormGroup: UntypedFormGroup;
	public currentDocument$: Observable<LexbeDoc>;
	public redactionEdits$: Observable<RedactionAnnotationEdit[]>;
	public annotationEdits$: Observable<RedactionAnnotationEdit[]>;
	public currentTab$: Observable<MainTabIndices>;
	public simpleState$: Observable<DocViewerSimpleState>;
	public redactEntirePage$: Observable<void>;
	public reset$: Observable<void>;
	public docNotesEdits$: Observable<DocNote>;
	public docList$: Observable<DocViewerContextDoc[]>;
	private resetSource: Subject<void> = new Subject<void>();
	private currentDocumentSource: ReplaySubject<LexbeDoc> = new ReplaySubject<LexbeDoc>(1);
	private redactionEditsSource: ReplaySubject<RedactionAnnotationEdit[]> = new ReplaySubject<RedactionAnnotationEdit[]>(1);
	private annotationEditsSource: ReplaySubject<RedactionAnnotationEdit[]> = new ReplaySubject<RedactionAnnotationEdit[]>(1);
	private docNotesEditsSource: BehaviorSubject<DocNote> = new BehaviorSubject<DocNote>(null);
	private currentTabSource: ReplaySubject<MainTabIndices> = new ReplaySubject<MainTabIndices>(1);
	private simpleStateSource: BehaviorSubject<DocViewerSimpleState> = new BehaviorSubject<DocViewerSimpleState>(null);
	private redactEntirePageSource: Subject<void> = new Subject<void>();
	private docListLocalStorageSource: ReplaySubject<DocViewerContextDoc[]> = new ReplaySubject<DocViewerContextDoc[]>(1);

	private readonly docListStorageKey: string = 'ContextMapping';

	constructor() {
		this.simpleState$ = this.simpleStateSource.pipe(
			distinctUntilChanged((a, b) => ComparisonUtility.isEqual(a, b)),
			map((snapshot) => cloneDeep(snapshot))
		);

		window.onstorage = (event) => {
			if (event.key === this.docListStorageKey)
				this.loadDocList();
		};

		this.docList$ = this.docListLocalStorageSource.pipe(
			map((docList) => cloneDeep(docList))
		);

		this.currentDocument$ = this.currentDocumentSource.pipe(
			map((document) => cloneDeep(document))
		);
		this.currentTab$ = this.currentTabSource.asObservable();
		this.redactionEdits$ = this.redactionEditsSource.asObservable();
		this.annotationEdits$ = this.annotationEditsSource.asObservable();
		this.docNotesEdits$ = this.docNotesEditsSource.asObservable();
		this.redactEntirePage$ = this.redactEntirePageSource.asObservable();
		this.reset$ = this.resetSource.asObservable();
		this.updateState();
	}

	reset() {
		this.documentDataFormGroup?.markAsPristine();
		this.resetSource.next(null);
	}

	public updateCurrentDocumentSource(document: LexbeDoc): void {
		this.currentDocumentSource.next(document);
	}

	public updateRedactionEditsSource(edit: RedactionAnnotationEdit[]): void {
		this.redactionEditsSource.next(edit);
	}

	public updateAnnotationEditsSource(edit: RedactionAnnotationEdit[]): void {
		this.annotationEditsSource.next(edit);
	}

	public updateDocNotesEditSource(edit: DocNote): void {
		this.docNotesEditsSource.next(edit);
	}

	public updateCurrentTabSource(tab: MainTabIndices): void {
		this.currentTabSource.next(tab);
	}

	public updateState(updateFunction?: StateUpdateFunction<DocViewerSimpleState>): Observable<void> {
		const updateComplete = new ReplaySubject<void>(1);

		this.simpleState$.pipe(
			take(1)
		).subscribe((current) => {
			if (!current) {
				current = new DocViewerSimpleState();
				// This is not ideal, but it will work until we decide to either make our "micro" stores more complex, or switch to a full state store.
				const shouldAutoAdvance = JSON.parse(localStorage.getItem('shouldAutoAdvance') || null);
				if (shouldAutoAdvance !== 'undefined' && shouldAutoAdvance !== null) {
					current.shouldAutoAdvance = shouldAutoAdvance;
				}

				const shouldPropagateCoding = JSON.parse(localStorage.getItem('shouldPropagateCoding') || null);
				if (shouldPropagateCoding !== 'undefined' && shouldPropagateCoding !== null) {
					current.shouldPropagateCoding = shouldPropagateCoding;
				}

				const compareDocumentId = localStorage.getItem('compareDocumentId');
				if (compareDocumentId !== 'undefined' && compareDocumentId) {
					current.compareDocumentId = compareDocumentId;
				}
			}

			const newState = updateFunction?.(current) ?? current;
			const clone = cloneDeep(newState);
			this.simpleStateSource.next(clone);
			updateComplete.next();
			updateComplete.complete();
		});

		return updateComplete;
	}

	public emitRedactEntirePageEvent(): void {
		this.redactEntirePageSource.next();
	}

	public loadDocList() {
		const docsStorageString = localStorage.getItem(this.docListStorageKey);
		const docsContextFromStorage = docsStorageString ? JSON.parse(docsStorageString) : null;
		if (docsContextFromStorage)
			this.docListLocalStorageSource.next(docsContextFromStorage.docList);
	}
}

class DocViewerSimpleState {
	public remainingReviewDocCount: number;
	public reviewedByMeCount: number;
	public shouldShowCompare: boolean;
	public compareDocumentId: string;
	public mode: 'viewer' | 'review' = 'viewer';
	public productionId: string;
	public navAvailable: boolean;
	public currentDocIndex: number;
	public totalDocs: number;
	public reviewSetId: string;
	public redactionEditorPageNumber: number = 1;
	public annotationEditorPageNumber: number = 1;
	public selectedRedactionId: number;
	public selectedAnnotationId: number;
	public hasOriginalRendering: boolean;
	public originalSizeMb: number;
	public hasPdfRendering: boolean;
	public pdfSizeMb: number;
	public hasProduction: boolean;
	public hasTranslation: boolean;
	public showOriginalSizeWarning: boolean;
	public showPdfSizeWarning: boolean;
	public shouldAutoAdvance: boolean = true;
	public shouldPropagateCoding: boolean = true;
	public currentPage: number;
	public pages: string[];
	public maxPage: number;
	public legacyParams: LegacyParams = new LegacyParams();
	public reviewStatus: number;
	public reviewedByMe: boolean;
	public reviewedByOthers: boolean;
	public hasUnsavedRedactionChanges: boolean = false;
	public hasUnsavedAnnotationChanges: boolean = false;
	public hasUnsavedCustomCodingChanges: boolean = false;
	public hasUnsavedNotes: boolean = false;
	public tabPreferences: TabPreference[];
	public notes: DocNote[];
	public custodians: Custodian[];
	public audits: Audit[];
	public folder: Folder;
	public emailThreadGroupingId: string;
	public reviewDocs: ReviewEntry[];
	public facts: Fact[];
	public issues: Issue[];
	public splitDoc: SplitDocResource;
	public lastChangeSet: DocChangeSetReferenceResource;
	public projectChangeSets: DocChangeSetReferenceResource[];
	public leftPanelState: 'expanded' | 'collapsed' = 'expanded';
	public rightPanelState: 'expanded' | 'collapsed' = 'expanded';
	public leftPanelWidth: number = 310;
	public isContextPanelResizing: boolean = false;
	public productionDocs: [string, string][];
}

class LegacyParams {
	public page: string;
	public fromSearch: boolean;
	public fromAutoRedaction: boolean;
	public prodId: string;
	public reviewSetId: string;
	public diffDocId: string;
	public redactionApplied: boolean;
	public redactionStatus: boolean;
	public annotationApplied: boolean;
	public annotationStatus: boolean;
}

type StateUpdateFunction<T> = (currentState: T) => T;
